import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { payoutService } from "@/services";
import { useBranding } from "@/context/BrandingContext";
import type { PayoutView } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Badge } from "@/components/base/Badge";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINRNumber } from "@/utils/format";
import { amountInWords } from "@/utils/collection";
import { paymentModeLabel } from "@/pages/collection/components/labels";
import { payoutStatusMeta } from "../components/labels";

export default function PayoutVoucherPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { businessName, tagline, effectiveLogo } = useBranding();
  const [payout, setPayout] = useState<PayoutView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setPayout(await payoutService.getPayout(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this voucher.");
      setPayout(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate(-1)}>
      Back
    </Button>
  );

  if (error) {
    return (
      <div>
        <PageHeader
          breadcrumb={
            <>
              <BreadcrumbItem label="Home" to="/" />
              <BreadcrumbItem label="Payout" to="/payout" />
              <BreadcrumbItem label="Voucher" isLast />
            </>
          }
          title="Payout voucher"
          actions={back}
        />
        <Card><ErrorState message={error} onRetry={load} /></Card>
      </div>
    );
  }

  if (!payout) {
    return (
      <div>
        <PageHeader
          breadcrumb={
            <>
              <BreadcrumbItem label="Home" to="/" />
              <BreadcrumbItem label="Payout" to="/payout" />
              <BreadcrumbItem label="Voucher" isLast />
            </>
          }
          title="Loading voucher…"
          actions={back}
        />
        <div className="h-72 animate-pulse rounded-lg bg-background-200" />
      </div>
    );
  }

  const meta = payoutStatusMeta(payout.status);
  const words = amountInWords(payout.amountPaid);
  const lastDisbursement = payout.disbursements[payout.disbursements.length - 1];
  const paidDate = lastDisbursement ? lastDisbursement.paidOn : payout.createdAt;

  return (
    <div>
      <PageHeader
        className="no-print"
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Payout" to="/payout" />
            <BreadcrumbItem label={`Voucher ${payout.voucherNumber}`} isLast />
          </>
        }
        title={`Voucher ${payout.voucherNumber}`}
        subtitle={`${payout.groupName} · Cycle ${payout.cycle} · ${payout.periodLabel}`}
        actions={back}
      />
      {payout.status === "reversed" ? (
        <div className="mb-3 no-print">
          <Alert variant="error" title="Reversed payout">
            Reversed on {formatDate(payout.reversedAt!)} by {payout.reversedBy}. Reason: {payout.reversalReason}. This voucher
            is void and its number is not reused.
          </Alert>
        </div>
      ) : null}

      <PrintLayout
        title="Payout Voucher"
        meta={`Voucher ${payout.voucherNumber} · ${formatDate(paidDate)}`}
        actions={
          <span className="inline-flex items-center gap-1 text-xs font-medium">
            <Badge color={meta.color} dot>{meta.label}</Badge>
          </span>
        }
      >
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={effectiveLogo} alt={businessName} className="h-10 w-10 object-contain rounded" />
              <div>
                <p className="text-base font-semibold text-foreground-950">{businessName}</p>
                <p className="text-xs text-foreground-500">{tagline}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="tnum text-sm font-semibold text-foreground-900">{payout.voucherNumber}</p>
              <p className="text-xs text-foreground-500">Date: {formatDate(paidDate)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 rounded-md border border-background-200 p-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-foreground-500">Pay to</p>
              <p className="mt-0.5 text-sm font-semibold text-foreground-900">{payout.winnerName}</p>
              <p className="tnum text-xs text-foreground-500">{payout.winnerCode} · Ticket {payout.winnerTicketNumber}</p>
              <p className="tnum text-xs text-foreground-500">{payout.winnerPhone} · {payout.winnerCity}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-foreground-500">Group / cycle</p>
              <p className="mt-0.5 text-sm font-semibold text-foreground-900">{payout.groupName}</p>
              <p className="text-xs text-foreground-500">Cycle {payout.cycle} — {payout.periodLabel}</p>
              <p className="tnum text-xs text-foreground-500">{payout.groupCode}</p>
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs uppercase tracking-wide text-foreground-500">Calculation</p>
            <table className="w-full border-collapse text-sm">
              <tbody>
                <tr className="border-b border-foreground-200/60">
                  <td className="py-1.5 pr-2">{payout.isOrganizerReserved ? "Prize (reserved cycle — full pool)" : "Prize (winning bid)"}</td>
                  <td className="tnum py-1.5 text-right">{formatINRNumber(payout.prize)}</td>
                </tr>
                {payout.sameGroupDues.map((d) => (
                  <tr key={d.cycle} className="border-b border-foreground-200/60">
                    <td className="py-1.5 pr-2">Less same-group due — cycle {d.cycle} ({d.periodLabel})</td>
                    <td className="tnum py-1.5 text-right text-danger-600">− {formatINRNumber(d.amount)}</td>
                  </tr>
                ))}
                {payout.manualDeductions.map((d) => (
                  <tr key={d.id} className="border-b border-foreground-200/60">
                    <td className="py-1.5 pr-2">Less deduction — {d.reason}</td>
                    <td className="tnum py-1.5 text-right text-danger-600">− {formatINRNumber(d.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground-900">
                  <td className="py-2 pr-2 font-semibold">Net payable</td>
                  <td className="tnum py-2 text-right text-base font-bold">{formatINRNumber(payout.netPayable)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {lastDisbursement ? (
            <div className="grid grid-cols-2 gap-4 rounded-md border border-background-200 p-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-foreground-500">Amount paid</p>
                <p className="tnum mt-0.5 text-lg font-bold text-foreground-950">{formatINRNumber(lastDisbursement.amount)}</p>
                {payout.balanceOwed > 0 ? (
                  <p className="tnum text-xs text-warning-700">Balance still owed: {formatINRNumber(payout.balanceOwed)}</p>
                ) : null}
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-foreground-500">Payment</p>
                <p className="mt-0.5 text-sm font-semibold text-foreground-900">{paymentModeLabel(lastDisbursement.mode)}</p>
                <p className="tnum text-xs text-foreground-500">
                  {lastDisbursement.reference ? `Ref: ${lastDisbursement.reference}` : "Cash"}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-foreground-500">No disbursement recorded yet — this payout has not been paid.</p>
          )}

          <div className="rounded-md bg-background-100/60 px-3 py-2.5">
            <p className="text-xs uppercase tracking-wide text-foreground-500">Amount in words</p>
            <p className="mt-0.5 text-sm font-medium text-foreground-900">{words}</p>
          </div>

          <p className="text-xs text-foreground-500">
            Approved by: <span className="font-medium text-foreground-900">{payout.approval?.approvedBy ?? "—"}</span>
            {payout.approval?.remarks ? <span className="ml-2">· {payout.approval.remarks}</span> : null}
          </p>

          <div className="mt-8 flex items-end justify-between border-t border-foreground-200 pt-8 print:break-inside-avoid">
            <div className="text-center">
              <p className="text-xs text-foreground-500">Authorised by</p>
              <p className="mt-8 text-sm font-medium text-foreground-900">{payout.approval?.approvedBy ?? ""}</p>
              <div className="mx-auto mt-1 h-px w-32 bg-foreground-300" />
            </div>
            <div className="text-center">
              <p className="text-xs text-foreground-500">Recipient signature</p>
              <div className="mt-6 h-8 w-40 border-b border-foreground-300" />
            </div>
          </div>
        </div>
      </PrintLayout>

      <style>{`@media print { @page { size: A4 portrait; margin: 14mm; } }`}</style>
    </div>
  );
}